import { useEffect, useState } from 'react';
import { Sparkles, Loader2, CheckCircle2, Search, Wand2, ShieldCheck, Clock } from 'lucide-react';

interface ArticleProgressCardProps {
  announcementId: string;
  style: string;
  provider: string;
  onComplete?: (articleId: string) => void;
}

interface ProgressState {
  status: string;
  step?: string;
  phase: 'generating' | 'fact_checking' | 'polishing' | 'language_review' | 'completed';
  progress: number;
  claimsVerified?: number;
  startTime: number;
  metadata?: any;
}

const PHASE_CONFIG = {
  generating: {
    icon: Sparkles,
    label: 'Generating Article',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    progress: 30,
  },
  fact_checking: {
    icon: Search,
    label: 'Fact-Checking Claims',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    progress: 65,
  },
  polishing: {
    icon: Wand2,
    label: 'Polishing for Coherence',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    progress: 75,
  },
  language_review: {
    icon: ShieldCheck,
    label: 'Voice Authenticity Review',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    progress: 90,
  },
  completed: {
    icon: CheckCircle2,
    label: 'Article Ready',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    progress: 100,
  },
};

export function ArticleProgressCard({
  announcementId,
  style,
  provider,
  onComplete,
}: ArticleProgressCardProps) {
  const [progressState, setProgressState] = useState<ProgressState>({
    status: 'generating',
    phase: 'generating',
    progress: 0,
    startTime: Date.now(),
  });
  const [elapsed, setElapsed] = useState(0);

  // Map backend status to phase
  const mapStatusToPhase = (status: string, step?: string): ProgressState['phase'] => {
    if (status === 'regenerating' || status === 'generating') return 'generating';
    if (status === 'fact_checking') return 'fact_checking';
    if (status === 'polishing') return 'polishing';
    if (status === 'language_review') return 'language_review';
    if (status === 'completed') return 'completed';
    return 'generating'; // Default
  };

  // Update elapsed time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - progressState.startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [progressState.startTime]);

  // Listen for WebSocket events (this will be connected in parent component)
  useEffect(() => {
    // This component receives updates via props or context
    // For now, we'll simulate with the phase transitions
  }, [announcementId]);

  const config = PHASE_CONFIG[progressState.phase];
  const Icon = config.icon;

  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getPhaseLabel = (): string => {
    switch (progressState.phase) {
      case 'generating':
        return 'Generating article content with AI...';
      case 'fact_checking':
        const claimsText = progressState.claimsVerified
          ? ` (${progressState.claimsVerified} claims verified)`
          : '';
        return `Verifying time-sensitive claims with Perplexity${claimsText}`;
      case 'polishing':
        return 'Reviewing coherence and style consistency...';
      case 'language_review':
        return 'Checking voice authenticity and removing AI patterns...';
      case 'completed':
        return 'Article generation complete!';
      default:
        return config.label;
    }
  };

  return (
    <div
      className={`p-5 border-2 ${config.borderColor} ${config.bgColor} rounded-xl transition-all shadow-sm`}
    >
      {/* Header */}
      <div className="flex items-start space-x-3 mb-4">
        <div className={`${config.color} mt-1`}>
          <Icon className="h-5 w-5 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-semibold ${config.color}`}>{config.label}</h3>
            {progressState.phase !== 'completed' && (
              <Loader2 className={`h-4 w-4 ${config.color} animate-spin`} />
            )}
          </div>
          <p className="text-sm text-grey-600">{getPhaseLabel()}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-medium text-grey-600">Progress</span>
          <span className="text-xs font-semibold text-grey-700">{config.progress}%</span>
        </div>
        <div className="w-full bg-grey-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ease-out ${
              progressState.phase === 'completed'
                ? 'bg-green-500'
                : progressState.phase === 'language_review'
                ? 'bg-amber-500'
                : progressState.phase === 'polishing'
                ? 'bg-indigo-500'
                : progressState.phase === 'fact_checking'
                ? 'bg-purple-500'
                : 'bg-blue-500'
            }`}
            style={{ width: `${config.progress}%` }}
          />
        </div>
      </div>

      {/* Phase Checklist */}
      <div className="space-y-2 mb-4 p-3 bg-surface/50 rounded-lg border border-grey-200/50">
        <PhaseItem
          phase="generating"
          currentPhase={progressState.phase}
          label="Article Generation"
        />
        <PhaseItem
          phase="fact_checking"
          currentPhase={progressState.phase}
          label="Fact-Checking"
        />
        <PhaseItem
          phase="polishing"
          currentPhase={progressState.phase}
          label="Coherence Polish"
        />
        <PhaseItem
          phase="language_review"
          currentPhase={progressState.phase}
          label="Voice Authenticity"
        />
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-grey-500">Style:</span>
          <div className="font-medium text-grey-900 mt-0.5">{style}</div>
        </div>
        <div>
          <span className="text-grey-500">Provider:</span>
          <div className="font-medium text-grey-900 mt-0.5">{provider}</div>
        </div>
        <div className="col-span-2">
          <span className="text-grey-500">Duration:</span>
          <div className="font-medium text-grey-900 mt-0.5 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatElapsed(elapsed)}
          </div>
        </div>
      </div>
    </div>
  );
}

function PhaseItem({
  phase,
  currentPhase,
  label,
}: {
  phase: ProgressState['phase'];
  currentPhase: ProgressState['phase'];
  label: string;
}) {
  const isCompleted =
    (phase === 'generating' && ['fact_checking', 'polishing', 'language_review', 'completed'].includes(currentPhase)) ||
    (phase === 'fact_checking' && ['polishing', 'language_review', 'completed'].includes(currentPhase)) ||
    (phase === 'polishing' && ['language_review', 'completed'].includes(currentPhase)) ||
    (phase === 'language_review' && currentPhase === 'completed');

  const isActive = phase === currentPhase;

  return (
    <div className="flex items-center gap-2 text-xs">
      {isCompleted ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
      ) : isActive ? (
        <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin" />
      ) : (
        <div className="h-3.5 w-3.5 rounded-full border-2 border-grey-300" />
      )}
      <span
        className={
          isCompleted
            ? 'text-green-700 font-medium'
            : isActive
            ? 'text-blue-700 font-medium'
            : 'text-grey-500'
        }
      >
        {label}
      </span>
    </div>
  );
}
